'use client';

import { FileUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { cn } from '~/lib/utils';

interface FileUploadDragAndDropProps {
  onFilesAccepted?: (files: File[]) => void;
  onFileRejected?: (rejections: FileRejection[]) => void;
  maxSize?: number; // in bytes
  acceptedFileTypes?: Record<string, string[]>;
  disabled?: boolean;
  text?: string;
  className?: string;
  children?: React.ReactNode;
}

const FileUploadDragAndDrop = ({
  onFilesAccepted,
  onFileRejected,
  maxSize = 10 * 1024 * 1024, // 10MB default
  acceptedFileTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
  }, // Default accepted file types (CHANGE AS NEEDED)
  disabled = false,
  className,
  text = 'Drag and drop a file here or click to browse',
  children,
}: FileUploadDragAndDropProps) => {
  const [, setUploadedFile] = useState<File | null>(null);

  // This function is called when files are dropped or selected
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        const singleFile = acceptedFiles[0];

        if (singleFile) {
          setUploadedFile(singleFile);
          onFilesAccepted?.([singleFile]); // handle accepted files
        }
      }

      if (rejectedFiles.length > 0) {
        onFileRejected?.(rejectedFiles); // handle rejected files
      }
    },
    [onFilesAccepted, onFileRejected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: 1,
    maxSize,
    disabled,
    multiple: false,
  });

  return (
    <div className={cn('w-full', className)}>
      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 cursor-pointer',
          disabled &&
            'cursor-not-allowed bg-surface-secondary border-content-border text-content-tertiary',
          'hover:border-gray-400 hover:bg-gray-100',
          isDragActive && 'scale-[1.02]',
        )}
      >
        <input {...getInputProps()} />

        {children ? (
          children
        ) : (
          <div className="flex flex-col items-center justify-center text-center">
            <div className="flex flex-col items-center justify-center mb-4 gap-4 p-10">
              <FileUp
                className={cn(
                  'text-content-primary b2 w-6 h-6',
                  'text-content-tertiary',
                )}
              />
              <div className="space-y-2">
                <p
                  className={cn(
                    'text-content-primary b2',
                    'text-content-tertiary',
                  )}
                >
                  {text}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadDragAndDrop;
